/**
 * Declared merge strategy for the object arrays inside a farm policy segment.
 *
 * Purpose: farm segments store deltas, and every merge site needs to know which field identifies an
 * item in an array. That knowledge used to live in four hand-maintained lists written in four
 * different vocabularies — `normalizedProviderDataArrayKeys` (farm-utils read),
 * `FULL_REPLACE_ARRAY_FIELDS` (policy-mod delta write), `FE_AUTHORITATIVE_RISK_*_FIELDS` (rater-v2
 * offer write) and the `changesArray[0].id` sniff in `mergeArraysByIdOrReplace`. None of them was
 * checked against the schema, each guessed differently when a field was unlisted, and two of them
 * were near-verbatim copies of each other. A copy that got a fix while its twin did not is what
 * produced #34355 (and, before it, #29374, #31476, #31790, #32575 and #32576).
 *
 * Flow: `getArrayMergePolicy(fieldName)` is the single answer. `read` drives delta reconstruction in
 * `applyFarmObjectDelta`; `write` drives how `calculateSegmentDelta` stores a changed array.
 *
 * Key decision: the two axes are declared separately because they are genuinely out of step today.
 * `scheduleCoverages` and `coverageF` already store the complete array (#31790) but are still read
 * back through the id-keyed path, which is wrong for entries keyed by `coverageType`. Fixing that
 * moves premium on in-force policies, so those stay `legacy-id` until a production scan and
 * underwriting review clear them. A `write: 'full-array'` paired with a `read: 'legacy-id'` is
 * precisely the list of pending migrations.
 *
 * Two known limits. Entries are keyed by bare field name, matching the existing dispatch in
 * `applyFarmObjectDelta`, so a name reachable under two parent types with different shapes gets one
 * strategy for both (`items` is the live case). And both `feAuthoritative` consumers scan
 * `revisedRisk.building` only, so an entry for an array living on `coverages` or at the risk root
 * would compile and do nothing at those sites.
 *
 * Issue: #34355. Prior art: #32575 (offer path), #31790, #32576, #31476.
 */

export type ArrayReadStrategy = { kind: 'replace' } | { kind: 'key'; key: string } | { kind: 'legacy-id' };

export type ArrayMergePolicy = {
  write: 'full-array' | 'positional-diff';
  read: ArrayReadStrategy;
  /**
   * The FE owns this list and sends it complete, so a post-`deepmerge` fixup must assign it verbatim
   * instead of letting deepmerge-ts concatenate. Declared rather than derived from
   * `read: 'replace'`: those two facts coincide for mortgagees but are independent, and a
   * backend-computed array that wanted a wholesale read replace must not silently start being
   * overwritten from the FE payload.
   */
  feAuthoritative?: boolean;
  /**
   * `read` is known to mis-key this field's entries, so the merge gives a wrong answer today. Set
   * only with a tracking issue. This exists so "declare a strategy and move on" cannot quietly
   * satisfy the schema invariant test with the broken strategy — that test requires the marker
   * whenever a `legacy-id` read meets an element type the schema says has no `id`.
   */
  readKnownWrong?: boolean;
};

const DEFAULT_POLICY: ArrayMergePolicy = { write: 'positional-diff', read: { kind: 'legacy-id' } };

/** Normalized property-provider responses key their entries by `attribute`, not `id`. */
export const PROVIDER_DATA_FIELDS = [
  'branch',
  'cape',
  'cherre',
  'coreLogic',
  'input',
  'smartyStreets',
  'verisk',
  'veriskRoofYear'
];

const POLICY_BY_FIELD: Record<string, ArrayMergePolicy> = {
  // `MortgageDetails` carries no `id` in the Dynamo type, in either GraphQL schema, or in staff-fe's
  // `MortgageDetailsInput`, and staff-fe rewrites every mortgageable building's list on each save.
  // The submitted list is authoritative, so store it whole and replace on read (#34355).
  mortgageDetails: { write: 'full-array', read: { kind: 'replace' }, feAuthoritative: true },

  // Stored whole since #31790, but still read back id-keyed, which is wrong for `coverageType`-keyed
  // entries: two schedule coverages on one risk merge into each other, and an emptied list does not
  // clear. Measured, not theoretical. Held back because `coverageType` feeds rating, so migrating
  // the read moves premium on in-force policies and needs a production scan first.
  scheduleCoverages: { write: 'full-array', read: { kind: 'legacy-id' }, readKnownWrong: true },
  coverageF: { write: 'full-array', read: { kind: 'legacy-id' }, readKnownWrong: true },

  // `RiskClarionDoorMap` has no `id` (it is keyed by `riskId`), and the list is a computed snapshot
  // of the ClarionDoor request ids a rating produced — every rating path writes the whole
  // `FarmClarionDoorMap` (offer/bind, policy-mod `setSegmentValues`, renewals), so a re-rated
  // segment's list replaces the prior one rather than merging id-less entries onto index 0 (#35385).
  risksClarionDoorIds: { write: 'full-array', read: { kind: 'replace' } },

  // Declared, marked wrong, not yet migrated. Each element type has no `id`, so the id-keyed read
  // collapses every delta entry onto base index 0 — the same mechanism as #34355. Held back because
  // they feed rating or billing and their stored deltas are genuinely sparse, so each needs a
  // production scan and underwriting review first.
  ...Object.fromEntries(
    [
      'branchPlusPolicies',
      'businessActivities',
      'businessPursuits',
      'cars',
      'coverages',
      'customFarmWorkItems',
      'emails',
      'invited',
      // Reachable both id-less (scheduledPersonalPropertyDetails.items) and id-bearing
      // (FarmScheduledPropertyDetails.items). Keying by bare field name cannot tell them apart, so
      // this entry describes the id-less one and is harmless for the other.
      'items',
      'otherPropertyAddresses',
      'phoneNumbers',
      'priorCars',
      'rentalPropertyAddresses',
      'trailers'
    ].map((field) => [field, { write: 'positional-diff', read: { kind: 'legacy-id' }, readKnownWrong: true }])
  ),

  ...Object.fromEntries(
    PROVIDER_DATA_FIELDS.map((field) => [field, { write: 'positional-diff', read: { kind: 'key', key: 'attribute' } }])
  )
};

/** Every field name the registry declares explicitly. Drives the schema invariant test. */
export const REGISTERED_ARRAY_FIELD_NAMES: readonly string[] = Object.freeze(Object.keys(POLICY_BY_FIELD));

/** Fields whose declared read strategy is known to give a wrong answer, pending migration. */
export const KNOWN_WRONG_READ_FIELD_NAMES: readonly string[] = Object.freeze(
  Object.entries(POLICY_BY_FIELD)
    .filter(([, policy]) => policy.readKnownWrong === true)
    .map(([fieldName]) => fieldName)
);

export function getArrayMergePolicy(fieldName: string): ArrayMergePolicy {
  return POLICY_BY_FIELD[fieldName] ?? DEFAULT_POLICY;
}

/** Field names whose changed array must be stored complete rather than as a positional diff. */
export const FULL_REPLACE_ARRAY_FIELD_NAMES: readonly string[] = Object.freeze(
  Object.entries(POLICY_BY_FIELD)
    .filter(([, policy]) => policy.write === 'full-array')
    .map(([fieldName]) => fieldName)
);

/**
 * Array fields the FE owns outright: it sends the complete list, so a post-`deepmerge` fixup must
 * assign it verbatim rather than let deepmerge-ts concatenate. Both the offer updater and the
 * policy-mod endorsement updater read this, so they cannot drift apart again.
 */
export const FE_AUTHORITATIVE_ARRAY_FIELD_NAMES: readonly string[] = Object.freeze(
  Object.entries(POLICY_BY_FIELD)
    .filter(([, policy]) => policy.feAuthoritative === true)
    .map(([fieldName]) => fieldName)
);

/**
 * Assign every FE-authoritative array the payload carries, overriding whatever `deepmerge` produced.
 *
 * Both the offer updater and the policy-mod endorsement updater call this, so they share the
 * *logic* and not merely the field list — sharing only the list still let a `null` guard land on one
 * copy and not its twin, which is the same drift that produced #34355.
 *
 * Presence, not truthiness: an empty list means "remove them all". `null` normalises to empty so the
 * preview and the stored policy cannot disagree. Scalars are deliberately not handled here — the
 * removable-coverage fields alongside these arrays must keep assigning `null` verbatim.
 *
 * `hasOwnProperty` rather than `Object.hasOwn`: policy-mod pins es2021.
 */
export function applyFeAuthoritativeArrayFields(
  target: Record<string, any> | null | undefined,
  submitted: Record<string, any> | null | undefined
): void {
  if (!target || !submitted) {
    return;
  }

  for (const field of FE_AUTHORITATIVE_ARRAY_FIELD_NAMES) {
    if (Object.prototype.hasOwnProperty.call(submitted, field)) {
      target[field] = submitted[field] ?? [];
    }
  }
}
